import { AppConstant, KeyConstant, SystemConstant } from "const";
import { uuid } from "utils";
import { useDispatch } from "react-redux";
import { isNil } from "lodash";
import { PRIVATE_FLAG_ID } from "pages/HomePage/components/AddingChat/AddingChannel/AddingChannelDrawer";
import { ConversationActions, GroupInfoActions } from "redux-store";
import { StorageUtil } from "utils";
import { getInteractor } from "services/local.service";
import { useCallback } from "react";
import { debounce } from "lodash";

const useCreateConversation = () => {
  const dispatch = useDispatch();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);

  const createConversation = async (memberList = [], groupName, groupType, option = {}) => {
    if (isNil(groupType)) {
      return false;
    }

    const privateFlagInput = document.getElementById(PRIVATE_FLAG_ID);
    const memberIdList = memberList.map(item => item.identifyId || item.contactId || item.id);

    // Adding current account_id if not exist
    if (false === memberIdList.includes(accountId)) {
      const currentAccount = await getInteractor(prefixKey).LocalAccountService.get(accountId, branchId);
      memberIdList.push(accountId);
      memberList.push(currentAccount);
    }

    if (!groupName) {
      groupName = await getInteractor(prefixKey).LocalGroupService.getGroupName({ group_type: groupType }, memberList);
    }

    const addingGroupData = {
      groupId: uuid(),
      groupType: groupType,
      groupName: groupName,
      name: JSON.stringify({ name: groupName }),
      members: memberIdList,
      privateF: privateFlagInput?.checked ? 1 : 0,
      encryptionF: SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION,

      ...option,
    };

    dispatch(GroupInfoActions.createGroup(addingGroupData));
  };

  const createGroup = (contactList = [], strGroupName, option = {}) =>
    createConversation(contactList, strGroupName, SystemConstant.GROUP_CHAT_TYPE.group, option);

  const createChannel = (contactList = [], strGroupName, option = {}) =>
    createConversation(contactList, strGroupName, SystemConstant.GROUP_CHAT_TYPE.channel, option);

  const createWithPersonal = useCallback(
    debounce(async (account, option = {}) => {
      const group = await getInteractor(prefixKey).LocalGroupService.findPersonalGroup(
        account.contactId || account.id,
        branchId,
      );
      if (group && group.id) {
        dispatch(
          ConversationActions.setSelectGroupId({
            selectedGroupId: group.id,
          }),
        );

        if (option.isDemandVoiceCall || option.isDemandVideoCall) {
          dispatch(
            ConversationActions.conversationSet({
              demandingVoiceCall: option.isDemandVoiceCall ? group.id : null,
              demandingVideoCall: option.isDemandVideoCall ? group.id : null,
            }),
          );
        }
      } else {
        // Create a tmp conversation if the user never chat before
        const otherMemberId = account.contactId || account.id;
        const otherMemberDetail = await getInteractor(prefixKey).LocalAccountService.get(otherMemberId);

        const newGroupId = uuid();
        const tmpGroupData = {
          id: newGroupId,
          name: JSON.stringify({ name: account.name }),
          created_by: accountId,
          branch_id: branchId,
          encryption_f: SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION,
          private_f: 0,
          state: SystemConstant.STATE.active,
          created: Date.now(),
          force_sync_f: 0,
          group_type: SystemConstant.GROUP_CHAT_TYPE.personal,
          options: JSON.stringify({
            draft_group_members: [accountId, account.contactId || account.id],
            is_bot: otherMemberDetail?.isBot ? 1 : 0,
          }),
        };

        await getInteractor(prefixKey).LocalGroupService.save([tmpGroupData]);

        dispatch(
          ConversationActions.setSelectGroupId({
            selectedGroupId: newGroupId,
          }),
        );

        if (option.isDemandVoiceCall || option.isDemandVideoCall) {
          dispatch(
            ConversationActions.conversationSet({
              demandingVoiceCall: option.isDemandVoiceCall ? group.id : null,
              demandingVideoCall: option.isDemandVideoCall ? group.id : null,
            }),
          );
        }
      }
    }, AppConstant.DEBOUNCE_TIME),
    [prefixKey],
  );

  return { createGroup, createChannel, createWithPersonal };
};

export default useCreateConversation;
